import React, { useState, useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  useMediaQuery,
  Tooltip,
  Typography,
  Avatar,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FinLogo } from "../../assets";

// MUI Icons
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import ImportContactsOutlinedIcon from "@mui/icons-material/ImportContactsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import StorageIcon from "@mui/icons-material/Storage";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { stringAvatar } from "../../utils";
import {
  StyledDrawer,
  UserSection,
  StyledListItemButton,
  StyledListItemIcon,
  StyledListItemText,
} from "./style";
import { setActiveMenuLabel } from "../../store/reducers/sideMenu";

const DRAWER_WIDTH = 280;
const DRAWER_COLLAPSED_WIDTH = 90;

export const Sidebar = ({ open, setOpen }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    const currentItem = MENU_ITEMS.find((item) => item.path === pathname);
    if (currentItem) {
      dispatch(setActiveMenuLabel(currentItem.label));
    }
  }, [pathname, dispatch]);

  const MENU_ITEMS = [
    {
      label: "Dashboard",
      path: "/",
      icon: <SpaceDashboardOutlinedIcon />,
      category: "main",
    },
    {
      label: "Product Catalogue",
      path: "/product-catalogue",
      icon: <MenuBookOutlinedIcon />,
      category: "apis",
    },
    {
      label: "API Catalogue",
      path: "/api-catalogue",
      icon: <ImportContactsOutlinedIcon />,
      category: "apis",
    },
    {
      label: "API Playground",
      path: "/api-playground",
      icon: <PlayArrowIcon />,
      category: "apis",
    },
    {
      label: "Documentation",
      path: "/documentation",
      icon: <DescriptionOutlinedIcon />,
      category: "apis",
    },
    {
      label: "API Credentials",
      path: "/api-credentials",
      icon: <VpnKeyIcon />,
      category: "management",
    },
    {
      label: "Credit Management",
      path: "/credit-management",
      icon: <AccountBalanceWalletIcon />,
      category: "management",
    },
    {
      label: "Usage Reports",
      path: "/usage-reports",
      icon: <AssessmentIcon />,
      category: "analytics",
    },
    {
      label: "Support",
      path: "/contact-us",
      icon: <SupportAgentIcon />,
      category: "support",
    },
  ];

  const CATEGORIES = {
    main: "Overview",
    apis: "API Services",
    management: "Management",
    analytics: "Analytics",
    support: "Support",
  };

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const isActive = (path) => pathname === path;

  const filteredMenuItems =
    user?.role?.toLowerCase() === "vkyc auditor"
      ? MENU_ITEMS.filter((item) => item.label === "Product Catalogue")
      : MENU_ITEMS;

  const renderMenuItems = (category) =>
    filteredMenuItems
      .filter((item) => item.category === category)
      .map((item) => {
        const isItemActive = isActive(item.path);
        return (
          <ListItem key={item.label} disablePadding>
            <Tooltip
              title={
                !open ? (
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 500, color: "#ffffff" }}
                  >
                    {item.label}
                  </Typography>
                ) : (
                  ""
                )
              }
              placement="right"
            >
              <StyledListItemButton
                open={open}
                isItemActive={isItemActive}
                onClick={() => {
                  navigate(item.path);
                  dispatch(setActiveMenuLabel(item.label));
                  if (isMobile) {
                    setOpen(false);
                  }
                }}
              >
                <StyledListItemIcon open={open}>
                  {React.cloneElement(item.icon, {
                    sx: { fontSize: "2.5rem" },
                  })}
                </StyledListItemIcon>
                <StyledListItemText
                  primary={item.label}
                  open={open}
                  isItemActive={isItemActive}
                />
              </StyledListItemButton>
            </Tooltip>
          </ListItem>
        );
      });

  const drawerContent = (
    <>
      {user && (
        <UserSection>
          <Box className="siderBarTop">
          <Box className="userName">
            <Box
              component="img"
              src={FinLogo}
              alt="Bank Verification"
              sx={{
                width: 40,  
                height: 40,
                objectFit: "contain",
              }}
            />
          </Box>
 
            {open && (
              <Box sx={{ flex: 1, minWidth: 0, marginLeft: "10px" }}>
                <Typography variant="h4">Finhub</Typography>
              </Box>
            )}
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                fontSize: "20px",
                color: "white",
                position: "absolute",
                top: "50%",
                right: open ? -18 : -22,
                transform: "translateY(-50%)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
              }}
            >
              {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Box>
        </UserSection>
      )}

      <Box sx={{ flex: 1, overflow: "auto", py: 1 }}>
        {Object.entries(CATEGORIES).map(([key]) => (
          <List key={key} sx={{ py: 0 }}>
            {renderMenuItems(key)}
          </List>
        ))}
      </Box>
    </>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { md: open ? DRAWER_WIDTH : DRAWER_COLLAPSED_WIDTH },
        flexShrink: { md: 0 },
      }}
    >
      {isMobile ? (
        <StyledDrawer
          variant="temporary"
          open={open}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          transitionDuration={100}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
              transition: theme.transitions.create("transform", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
        >
          {drawerContent}
        </StyledDrawer>
      ) : (
        <StyledDrawer
          variant="persistent"
          open={open}
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: open ? DRAWER_WIDTH : DRAWER_COLLAPSED_WIDTH,
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
        >
          {drawerContent}
        </StyledDrawer>
      )}
    </Box>
  );
};
